<template lang="pug">
v-card.custom-card(outlined)
  template(v-if="ativo")
    .replicacao-header.d-flex.align-center.justify-space-between.gap-2
      .replicacao-titulo {{ titulo }}
      v-btn.btn-action.text-none(
        small,
        outlined,
        :disabled="somenteLeitura",
        @click="alternarTodas"
      ) {{ todasMarcadas ? textoDesmarcarTodos : textoMarcarTodos }}
    .replicacao-subtitle.pb-2.pt-2(v-if="nomeItem") {{ prefixoItem }}: {{ nomeItem }}
    v-divider
    .replicacao-lista
      .replicacao-grupo(v-for="grupo in grupos", :key="grupo.chave")
        //- O checkbox pai encabeça o bloco, dispensando um título repetido
        v-checkbox.replicacao-check.replicacao-check--pai.ma-0.pa-0(
          v-if="grupo.pai",
          :input-value="!!value[grupo.pai.chave]",
          :label="grupo.pai.rotulo",
          :disabled="somenteLeitura || !estaHabilitado(grupo.pai)",
          dense,
          hide-details,
          @change="alternar(grupo.pai.chave, $event)"
        )
        .replicacao-item(
          v-for="opcao in grupo.opcoes",
          :key="opcao.chave",
          :class="{ 'replicacao-item--filho': !!opcao.dependeDe }"
        )
          v-checkbox.replicacao-check.ma-0.pa-0(
            :input-value="!!value[opcao.chave]",
            :label="opcao.rotulo",
            :disabled="somenteLeitura || !estaHabilitado(opcao)",
            dense,
            hide-details,
            @change="alternar(opcao.chave, $event)"
          )

  //- Estado vazio: nada selecionado
  .replicacao-empty.text-center.py-8(v-else)
    v-icon.empty-state-icon(size="48", color="blue lighten-3") {{ iconeSemSelecao }}
    .empty-state-subtitle.mt-4 {{ textoSemSelecao }}
</template>

<script>
// Grupo "1 - Individual" não possui contas correntes nem impFinanceiroCaixa próprias.
const GRUPO_INDIVIDUAL = 1;

export default {
  name: "ReplicacaoEmpresaCard",

  props: {
    titulo: {
      type: String,
      default: "Opções de replicação"
    },
    // Dicionário "chave da opção" -> booleano (v-model).
    value: {
      type: Object,
      default: () => ({})
    },
    ativo: {
      type: Boolean,
      default: false
    },
    somenteLeitura: {
      type: Boolean,
      default: false
    },
    // Grupo da empresa em cadastro. Individual desabilita contas e impFinanceiroCaixa.
    grupoEmpresa: {
      type: [String, Number],
      default: null
    },
    nomeItem: {
      type: String,
      default: ""
    },
    prefixoItem: {
      type: String,
      default: "Empresa"
    },
    textoMarcarTodos: {
      type: String,
      default: "Marcar Todos"
    },
    textoDesmarcarTodos: {
      type: String,
      default: "Desmarcar Todos"
    },
    iconeSemSelecao: {
      type: String,
      default: "mdi-content-duplicate"
    },
    textoSemSelecao: {
      type: String,
      default: "Selecione um cliente e empresa para configurar a replicação"
    }
  },

  computed: {
    // Definição declarativa das opções. "dependeDe" aponta a opção pai e
    // "exigeGrupoNaoIndividual" marca o que não se aplica ao grupo Individual.
    grupos() {
      return [
        {
          chave: "impUsuarios",
          pai: { chave: "impUsuarios", rotulo: "Usuários" },
          opcoes: [
            {
              chave: "impUsuariosAcessos",
              rotulo: "Acessos dos usuários",
              dependeDe: "impUsuarios"
            }
          ]
        },
        {
          chave: "planoContas",
          // Sem filho marcado o pai não tem efeito: desmarca junto com o último filho.
          pai: { chave: "planoContas", rotulo: "Plano de Contas", desmarcaSemFilhos: true },
          opcoes: [
            { chave: "impPlanoContasAnalitico", rotulo: "Analítico", dependeDe: "planoContas" },
            { chave: "impPlanoContasGerencial", rotulo: "Gerencial", dependeDe: "planoContas" },
            {
              chave: "impPlanoContasLigacoesAnaliGer",
              rotulo: "Ligações Analítico × Gerencial",
              dependeDe: "planoContas"
            },
            // DRE só vale com Analítico marcado (além do pai Plano de Contas).
            {
              chave: "impDRE",
              rotulo: "DRE",
              dependeDe: "planoContas",
              exigeMarcada: "impPlanoContasAnalitico"
            }
          ]
        },
        {
          chave: "cadastros",
          opcoes: [
            { chave: "impCidades", rotulo: "Cidades" },
            { chave: "impProdutos", rotulo: "Produtos/Produtos Comercializados" },
            {
              chave: "impFinanceiroContaCorrente",
              rotulo: "Contas Correntes da Empresa",
              exigeGrupoNaoIndividual: true
            },
            {
              chave: "impFinanceiroCaixa",
              rotulo: "Caixas da Empresa",
              exigeGrupoNaoIndividual: true
            },
            { chave: "impCentroCustosAutomatico", rotulo: "Centro de Custos Automático" },
            {
              chave: "impAgenteComercial",
              rotulo: "Agente Comercial e Agentes Comerciais da Safra"
            },
            { chave: "impArmazens", rotulo: "Armazéns" },
            { chave: "impComercializacaoCustos", rotulo: "Custos de Comercialização" },
            {
              chave: "impDadosAdicionaisFiscoContrib",
              rotulo: "Dados Adicionais do Fisco e Contribuinte"
            }
          ]
        }
      ];
    },

    // Inclui os pais, que encabeçam os blocos, antes dos respectivos filhos.
    // A ordem importa: a cascata é resolvida numa única passada.
    todasOpcoes() {
      return this.grupos.reduce((lista, grupo) => {
        if (grupo.pai) lista.push(grupo.pai);
        return lista.concat(grupo.opcoes);
      }, []);
    },

    opcoesHabilitadas() {
      return this.todasOpcoes.filter(opcao => this.estaHabilitado(opcao));
    },

    // Considera apenas o que o usuário pode manipular no momento.
    todasMarcadas() {
      const habilitadas = this.opcoesHabilitadas;
      if (!habilitadas.length) return false;
      return habilitadas.every(opcao => !!this.value[opcao.chave]);
    }
  },

  methods: {
    // Uma opção só é editável se o pai estiver marcado e, quando exigido, se o
    // grupo da empresa for diferente de Individual. O estado avaliado pode ser
    // o objeto em construção, permitindo resolver a cascata numa única passada.
    estaHabilitado(opcao, estado) {
      const atual = estado || this.value;
      if (opcao.dependeDe && !atual[opcao.dependeDe]) return false;
      if (opcao.exigeMarcada && !atual[opcao.exigeMarcada]) return false;
      if (opcao.exigeGrupoNaoIndividual && this.grupoEhIndividual()) return false;
      return true;
    },

    grupoEhIndividual() {
      if (this.grupoEmpresa === null || this.grupoEmpresa === undefined) return true;
      return Number(this.grupoEmpresa) === GRUPO_INDIVIDUAL;
    },

    // Desmarcar um pai desmarca em cascata as opções que dependem dele.
    alternar(chave, marcado) {
      const novo = { ...this.value, [chave]: !!marcado };

      // Marcar o pai marca todos os filhos aplicaveis; desmarcar limpa todos.
      this.todasOpcoes
        .filter(opcao => opcao.dependeDe === chave)
        .forEach(opcao => {
          novo[opcao.chave] = marcado ? this.estaHabilitado(opcao, novo) : false;
        });

      // Desmarcar uma opção desmarca (e desabilita) as que exigem que ela esteja marcada.
      if (!marcado) {
        this.todasOpcoes
          .filter(opcao => opcao.exigeMarcada === chave)
          .forEach(opcao => {
            novo[opcao.chave] = false;
          });
      }

      // Desmarcou o último filho de um pai que não funciona sozinho: desmarca o pai.
      if (!marcado) {
        const opcao = this.todasOpcoes.find(o => o.chave === chave);
        const pai =
          opcao && opcao.dependeDe && this.todasOpcoes.find(o => o.chave === opcao.dependeDe);
        if (pai && pai.desmarcaSemFilhos) {
          const filhos = this.todasOpcoes.filter(o => o.dependeDe === pai.chave);
          if (!filhos.some(f => novo[f.chave])) novo[pai.chave] = false;
        }
      }

      this.$emit("input", novo);
    },

    // Marca ou desmarca somente o que está habilitado no momento. Os pais são
    // avaliados antes dos filhos, então "novo" já reflete a decisão do pai
    // quando o filho é processado.
    alternarTodas() {
      const marcar = !this.todasMarcadas;
      const novo = { ...this.value };

      this.todasOpcoes.forEach(opcao => {
        novo[opcao.chave] = this.estaHabilitado(opcao, novo) ? marcar : false;
      });

      this.$emit("input", novo);
    },

    // Mantém o dicionário coerente quando uma dependência deixa de valer.
    _sincronizarDependencias() {
      const novo = { ...this.value };
      let alterou = false;

      this.todasOpcoes.forEach(opcao => {
        if (!this.estaHabilitado(opcao, novo) && novo[opcao.chave]) {
          novo[opcao.chave] = false;
          alterou = true;
        }
      });

      if (alterou) this.$emit("input", novo);
    }
  },

  watch: {
    // Trocar o grupo para Individual desmarca contas correntes e impFinanceiroCaixa.
    grupoEmpresa() {
      this._sincronizarDependencias();
    }
  }
};
</script>

<style scoped>
.custom-card {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background-color: #fff;
  border: 1px solid #d9e2ec !important;
  border-radius: 18px 6px 18px 6px !important;
  box-shadow: 0 2px 8px rgba(15, 42, 77, 0.08) !important;
}

.btn-action {
  height: 30px !important;
  min-width: 0 !important;
  padding: 0 12px !important;
  font-size: 12px !important;
  letter-spacing: 0.01em !important;
  border: 1px solid #d0d7de !important;
  color: #57606a !important;
  background-color: #ffffff !important;
  font-weight: 500 !important;
  border-radius: 6px !important;
}

.text-none {
  text-transform: none !important;
}

.replicacao-header {
  flex: 0 0 auto;
  min-height: 56px;
  padding: 0 18px;
  display: flex;
  align-items: center;
}

.gap-2 {
  gap: 8px;
}

.replicacao-titulo {
  color: #0f2b57;
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
}

.replicacao-subtitle {
  flex: 0 0 auto;
  padding: 0 18px;
  font-size: 12px;
  color: #1976d2;
  font-weight: 600;
}

.replicacao-lista {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  scrollbar-width: thin;
  padding: 12px;
}

/* Bloco de grupo, no mesmo padrão do cadastro da empresa */
.replicacao-grupo {
  border: 1px solid #e8eef4;
  border-radius: 8px;
  padding: 12px;
  background-color: #fbfdff;
}

.replicacao-grupo + .replicacao-grupo {
  margin-top: 12px;
}

/* O checkbox pai faz as vezes de título do bloco */
.replicacao-check--pai {
  margin-bottom: 8px !important;
}

.replicacao-check--pai >>> .v-label {
  font-weight: 700;
  color: #0f2b57;
}

/* Dependentes recuados, evidenciando a hierarquia */
.replicacao-item--filho {
  padding-left: 20px;
}

.replicacao-item + .replicacao-item {
  margin-top: 6px;
}

.replicacao-check >>> .v-label {
  font-size: 12px;
  color: #444;
  line-height: 1.3;
}

.replicacao-check >>> .v-input--selection-controls__input {
  margin-right: 6px;
}

.replicacao-empty {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 24px;
}

.empty-state-icon {
  opacity: 0.6;
}

.empty-state-subtitle {
  font-size: 12px;
  color: #777;
  max-width: 260px;
  margin: 0 auto;
  line-height: 1.4;
}
</style>
